import React from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import { KeyboardDatePicker } from "@material-ui/pickers";
import LoadableButton from "src/components/LoadableButton";
import { updateIntegrationLastSyncDates } from "src/store/support/actions";

export interface SyncDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  integrationId: string;
  lastSyncDateInventory?: string | null;
  lastSyncDateSales?: string | null;
  /** When false, inventory sync date is not shown/updated (e.g. Whatnot). */
  enableInventory?: boolean;
}

function formatDate(date: Date): string {
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((d) => `${d}`.padStart(2, "0"))
    .join("-");
}

function toDate(str?: string | null): Date {
  if (!str) return new Date();
  if (str.includes("T")) return new Date(str);
  return new Date(`${str}T12:00:00.000Z`);
}

function DateField({
  label,
  value,
  onChange,
  disabled,
}: {
  disabled?: boolean;
  label: string;
  value: Date;
  onChange: (d: Date) => void;
}) {
  return (
    <KeyboardDatePicker
      fullWidth
      variant="inline"
      inputVariant="outlined"
      disableToolbar
      format="MM/dd/yyyy"
      label={label}
      required
      autoOk
      value={value}
      disabled={disabled}
      onChange={(e: any) => {
        const date = new Date(e);
        onChange(date);
      }}
    />
  );
}

function SyncDetailsDialogContent({
  integrationId,
  lastSyncDateSales,
  lastSyncDateInventory,
  enableInventory = true,
  onClose,
}: Omit<SyncDetailsDialogProps, "open">) {
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      lastSyncDateSales: toDate(lastSyncDateSales),
      lastSyncDateInventory: toDate(lastSyncDateInventory),
    },
    onSubmit: async (values) => {
      try {
        await dispatch(
          updateIntegrationLastSyncDates(integrationId, {
            ...(enableInventory
              ? { inventory: formatDate(values.lastSyncDateInventory) }
              : {}),
            sales: formatDate(values.lastSyncDateSales),
          })
        );
        onClose();
      } catch (e) {
        toast.error((e as Error).message || String(e));
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} noValidate autoComplete="off">
      <DialogContent>
        {enableInventory ? (
          <DateField
            label="Sync Inventory Since"
            value={formik.values.lastSyncDateInventory}
            disabled={formik.isSubmitting}
            onChange={(e: any) => {
              const date = new Date(e);
              formik.setFieldValue("lastSyncDateInventory", date);
            }}
          />
        ) : null}
        <Box mt={enableInventory ? 2 : 0}>
          <DateField
            label="Sync Sales and Fees Since"
            value={formik.values.lastSyncDateSales}
            disabled={formik.isSubmitting}
            onChange={(e: any) => {
              const date = new Date(e);
              formik.setFieldValue("lastSyncDateSales", date);
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadableButton
          type="submit"
          loading={formik.isSubmitting}
          color="primary"
          variant="contained"
        >
          Update
        </LoadableButton>
      </DialogActions>
    </form>
  );
}
function SyncDetailsDialog({
  open,
  onClose,
  integrationId,
  lastSyncDateInventory,
  lastSyncDateSales,
  enableInventory = true,
}: SyncDetailsDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Sync Details</DialogTitle>
      <SyncDetailsDialogContent
        integrationId={integrationId}
        lastSyncDateInventory={lastSyncDateInventory}
        lastSyncDateSales={lastSyncDateSales}
        enableInventory={enableInventory}
        onClose={onClose}
      />
    </Dialog>
  );
}

export default SyncDetailsDialog;
